<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@unhead/vue';

import AppToast from '#/components/layout/AppToast.vue';
import { useCanonicalHead } from '#/composables/useCanonicalHead';
import { brand } from '#/config/brand';
import { useRouteHead } from '#/composables/useRouteHead';
import BlankLayout from '#/layouts/BlankLayout.vue';
import DefaultLayout from '#/layouts/DefaultLayout.vue';
import WorkspaceLayout from '#/layouts/WorkspaceLayout.vue';

/**
 * 레이아웃 리졸버.
 * route.meta.layout('blank' | 'workspace' | 'default'[기본])에 따라 셸 레이아웃을 선택하고
 * <RouterView/>(페이지)를 레이아웃 slot 에 주입한다.
 * - default   : 1차 커머스 셸(TopStrip+AppHeader+Footer).
 * - blank     : 셸 없음(인증·예외).
 * - workspace : 2차 AI 분석 셸(WorkspaceShell GNB, 커머스와 분리).
 *
 * AppToast 는 레이아웃 밖에 1개만 마운트 — 라우트 이동에도 살아남는 전역 알림 단일 표시기
 * (http.ts 인터셉터가 띄우는 에러 토스트의 유일한 렌더 지점).
 *
 * SEO: 공개 라우트의 canonical `<link>` 는 useCanonicalHead 가, 문서 제목·설명은 useRouteHead 가
 * 라우트 meta 기반으로 여기서 한 번만 주입한다(가이드는 데이터 기준 자체 head — 컴포저블이 양보).
 * 페이지별 중복 선언 금지.
 */
const route = useRoute();

useRouteHead();
useCanonicalHead();

const LAYOUTS = { blank: BlankLayout, workspace: WorkspaceLayout, default: DefaultLayout } as const;

const layoutComponent = computed(
  () => LAYOUTS[route.meta.layout as keyof typeof LAYOUTS] ?? DefaultLayout,
);

/**
 * 팔레트 테마 부착(SSoT — 여기 한 곳). /analysis/** 는 워크스페이스(Green, tokens.workspace.css),
 * 그 외 전부 커머스(Navy, tokens.commerce.css `:root` 기본값). 레이아웃이 아니라 <html data-theme>
 * 에 붙이는 이유: 검사 응시(/analysis/tests/*)는 blank 레이아웃이고, 모달(teleport→body)·토스트도
 * 레이아웃 밖에 있어 루트 속성이어야 같은 팔레트를 본다. 경로 기준은 brand.crossLinks(두 축 SSoT).
 *
 * `document` 를 직접 만지지 않고 `useHead({ htmlAttrs })` 로 선언한다 — `vite-ssg build` 는 공개 라우트를
 * 서버에서 렌더하므로 setup 단계의 document 접근은 프리렌더를 깨뜨린다. head 선언이면 SSR 안전하고,
 * 프리렌더된 /analysis HTML 에도 `data-theme` 이 실려 hydration 전 첫 페인트부터 그린 팔레트다.
 */
const WORKSPACE_PREFIX = brand.crossLinks.analysis;
const isWorkspace = computed(
  () => route.path === WORKSPACE_PREFIX || route.path.startsWith(`${WORKSPACE_PREFIX}/`),
);
useHead({
  htmlAttrs: { 'data-theme': computed(() => (isWorkspace.value ? 'workspace' : undefined)) },
});
</script>

<template>
  <component :is="layoutComponent">
    <RouterView v-slot="{ Component }">
      <!-- w-full: BlankLayout 의 flex 자식이 shrink-to-fit 으로 좁아져 내부 카드 max-w-[440px]
           가 무력화되는 것 방지. 중앙정렬은 레이아웃(BlankLayout)이 담당 — wrapper 에 items-center
           를 주면 DefaultLayout 섹션 배경이 content 폭으로 수축(full-bleed 깨짐)되므로 주지 않는다. -->
      <div :key="$route.path" class="w-full animate-fo-fade">
        <component :is="Component" />
      </div>
    </RouterView>
  </component>

  <AppToast />
</template>
